import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Textarea } from "../components/ui/Textarea";
import { Card, CardBody } from "../components/ui/Card";
import { Wordmark } from "../components/Brand";
import { Turnstile } from "../components/Turnstile";
import { TURNSTILE_SITE_KEY } from "../config/turnstile";
import { api, ApiException } from "../lib/api";

export function RequestAccess() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Just a moment — finishing the bot check.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/access-request", {
        name,
        email,
        message: message || null,
        turnstileToken,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Couldn't send the request.");
      setTurnstileToken(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto max-w-5xl px-5 h-14 flex items-center">
          <Link to="/" className="block"><Wordmark size="sm" /></Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <Card className="w-full max-w-md">
          <CardBody>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-9 w-9 rounded-lg bg-primary-soft flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-primary" />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">Start your family's circle</h1>
            </div>

            {done ? (
              <>
                <p className="mt-2 text-text-muted">
                  Got it — your request is in. We'll get back to you within a day or two with a sign-in link to <strong>your family's own private circle</strong>. It's yours alone — separate from every other family on Carol Care.
                </p>
                <div className="mt-6">
                  <Link to="/"><Button variant="secondary">Back to home</Button></Link>
                </div>
              </>
            ) : (
              <>
                <p className="mt-1 text-sm text-text-muted">
                  Carol Care gives every family their own <strong>private</strong> circle for coordinating medical visits. You're not joining anyone else's family — you're getting a brand-new space that only the people <em>you</em> invite can see. Tell us a little about you and we'll set it up within a day or two.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                  <div>
                    <Label htmlFor="ra-name">Your name</Label>
                    <Input id="ra-name" required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="ra-email">Email</Label>
                    <Input
                      id="ra-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ra-msg">A sentence about your situation <span className="text-text-faint font-normal">(optional)</span></Label>
                    <Textarea
                      id="ra-msg"
                      rows={3}
                      placeholder={`e.g., "Helping my dad with appointments — three siblings spread across the country."`}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-text-faint leading-relaxed">
                      <strong className="text-text-muted">Why we ask:</strong> a sentence helps us tell real people from spam bots before we open accounts. Your details stay with us — they're never shared with any other family. Once you're in, your circle is fully private and only the people you invite can see anything in it.
                    </p>
                  </div>

                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onVerify={(t) => setTurnstileToken(t)}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                  />

                  {error && (
                    <div role="alert" className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">{error}</div>
                  )}

                  <Button type="submit" size="lg" loading={busy} disabled={!turnstileToken} className="w-full">Send request</Button>
                </form>
                <p className="mt-4 text-xs text-text-faint">
                  Submitting this does not grant access — an admin must approve it. Your details are only visible to admins.
                </p>
              </>
            )}
          </CardBody>
        </Card>
      </main>
    </div>
  );
}
